import { useState } from "react";
import { Link } from "react-router-dom";
import { PenLine, Loader2, CheckCircle2, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = [
  "Psychologue",
  "Logopède / Orthophoniste",
  "Éducateur de jeunes enfants",
  "Auxiliaire de puériculture",
  "Direction de structure",
  "Santé / Pédiatrie",
  "Autre métier de l'enfance",
];

const MAX_CHARS = 500;

export const ExpertSubmission = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showConditions, setShowConditions] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ title: "", category: "", content: "", expert_link: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.content.trim().length < 50) {
      toast.error("Conseil trop court", { description: "Votre conseil doit contenir au moins 50 caractères." });
      return;
    }
    setLoading(true);
    try {
      await http.post("/blog/submissions", form);
      setSent(true);
      toast.success("Conseil envoyé", { description: "Merci ! Il sera publié après validation par notre équipe." });
    } catch (err) {
      toast.error("Erreur", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  return (
    <div className="card-soft p-8 relative overflow-hidden mb-12" data-testid="expert-submission-block">
      <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-brand-500/10 blur-2xl pointer-events-none" />
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="grid place-items-center h-12 w-12 rounded-2xl bg-brand-500 text-white shrink-0"><PenLine size={20} strokeWidth={1.75} /></div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-xl text-slate-900">Contribuer à la communauté</h2>
          <p className="text-sm text-slate-600 mt-1">Partagez une réflexion, une pratique ou un article avec la communauté Beehive Academy. Les contributions sélectionnées pourront être partagées avec notre communauté afin de diffuser les connaissances scientifiques et de nourrir la réflexion collective.</p>
          <button type="button" onClick={() => setShowConditions(!showConditions)} data-testid="expert-conditions-toggle" className="mt-2 text-sm font-semibold text-brand-700 hover:text-brand-800 underline underline-offset-4">
            {showConditions ? "Masquer les conditions" : "Voir les conditions de contribution"}
          </button>
        </div>
        {user && user !== false ? (
          !sent && (
            <button onClick={() => setOpen(!open)} data-testid="expert-submit-toggle" className="btn-primary text-sm shrink-0">
              {open ? "Fermer" : "Proposer un article"}
            </button>
          )
        ) : (
          <Link to="/connexion" data-testid="expert-login-link" className="btn-primary text-sm shrink-0">Se connecter pour contribuer</Link>
        )}
      </div>

      {showConditions && (
        <div className="mt-6 rounded-2xl bg-brand-50/60 border border-brand-100 p-6" data-testid="expert-conditions-panel">
          <h3 className="font-display font-bold text-slate-900">Conditions de contribution</h3>
          <ul className="mt-3 space-y-3 text-sm text-slate-700">
            <li className="flex gap-3"><BadgeCheck size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} /><span>La contribution doit être en lien avec l'enfance, le développement ou l'éducation.</span></li>
            <li className="flex gap-3"><BadgeCheck size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} /><span>Elle doit s'appuyer sur une expertise, une expérience ou des connaissances pertinentes.</span></li>
            <li className="flex gap-3"><BadgeCheck size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} /><span>La contribution doit être originale et respecter les droits d'auteur.</span></li>
            <li className="flex gap-3"><BadgeCheck size={16} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} /><span>Beehive Academy se réserve la possibilité de sélectionner les contributions publiées.</span></li>
          </ul>
          <p className="text-xs text-slate-500 mt-4">Chaque contribution est relue par notre équipe. Les contributions sélectionnées pourront être partagées avec la communauté.</p>
        </div>
      )}

      {sent && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 p-4" data-testid="expert-submit-success">
          <CheckCircle2 className="text-emerald-600 shrink-0" strokeWidth={1.75} />
          <p className="text-sm text-slate-700">Merci pour votre contribution ! Elle sera relue par notre équipe et pourra être partagée avec la communauté.</p>
        </div>
      )}

      {open && !sent && user && (
        <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="expert-submit-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input data-testid="expert-title" required placeholder="Titre du conseil" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <select
              data-testid="expert-category"
              required
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring text-slate-700"
            >
              <option value="" disabled>Catégorie / Métier</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Textarea
              data-testid="expert-content"
              required
              rows={5}
              maxLength={MAX_CHARS}
              placeholder="Votre conseil : 1 conseil opérationnel, 0 blabla, moins de 500 caractères."
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value.slice(0, MAX_CHARS) })}
            />
            <div className="mt-1 text-right text-xs text-slate-500" data-testid="expert-char-counter">{form.content.length} / {MAX_CHARS} caractères</div>
          </div>
          <Input data-testid="expert-link" type="url" placeholder="Lien vers votre site ou LinkedIn (facultatif)" value={form.expert_link} onChange={(e) => setForm({ ...form, expert_link: e.target.value })} />
          <button data-testid="expert-submit-btn" disabled={loading} className="btn-primary w-full sm:w-auto disabled:opacity-70">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Envoyer pour validation"}
          </button>
        </form>
      )}
    </div>
  );
};
